import * as React from 'react';
import { useState } from 'react';
import { Button } from '@mui/material';
import { useRecordContext, useDataProvider, useNotify, useRefresh } from 'react-admin';
import { CreditCard } from '../index';

/** Company id the Privacy.com pause/resume integration applies to. */
const PRIVACY_MAIN_COMPANY_ID = 'privacy-main';

/**
 * Datagrid action cell for the Credit Cards list. Renders a "Pause" button for `OPEN`
 * privacy-main cards and a "Resume" button for `PAUSED` privacy-main cards, calling the
 * backend `CreditCards/Pause/{id}` or `CreditCards/Resume/{id}` endpoints. Renders nothing
 * for cards that don't belong to the `privacy-main` company, or whose status is neither
 * `OPEN` nor `PAUSED`.
 */
const PauseResumeAction = (): JSX.Element | null => {
    const record = useRecordContext<CreditCard>();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [isLoading, setIsLoading] = useState(false);

    if (!record || record.companyId !== PRIVACY_MAIN_COMPANY_ID) return null;
    if (record.cardStatus !== 'OPEN' && record.cardStatus !== 'PAUSED') return null;

    const action = record.cardStatus === 'OPEN' ? 'Pause' : 'Resume';

    /**
     * Calls the backend action for the current card and refreshes the grid's row data on success.
     *
     * `DataProvider.postData` swallows HTTP errors (`.catch((error) => error)`) instead of
     * rejecting, so a failed request (e.g. a `502` from a missing `SONG_PRIVACY_KEY`) still
     * resolves this promise — with the raw `HttpError` in place of `{ data }`. `response?.data`
     * must therefore be checked explicitly; otherwise every response, including error ones, is
     * reported as a success (see `.catch`-based error handling elsewhere in this codebase, e.g.
     * `RulePopup.js`, for the same pattern).
     *
     * On success this calls `useRefresh()` rather than mutating `record.cardStatus` directly:
     * `record` here is the object react-admin's `<List>` holds in its React Query cache, and
     * mutating a field on it does not change the object reference or call `setState`, so the
     * `Datagrid` never re-renders and the row kept showing the pre-pause/resume status (the
     * reported bug). `refresh()` re-fetches the list from `CreditCards/Filter`, which returns
     * the confirmed post-refresh state persisted by the backend (see `CreditCardsController.
     * SetCardState`), so the row reflects Privacy.com's actual recorded state.
     */
    const handleClick = (): void => {
        setIsLoading(true);
        dataProvider
            .postData('CreditCards', `${action}/${record.id}`, {}, 'POST')
            .then((response) => {
                setIsLoading(false);
                // `response?.data` is checked (not just that the promise resolved) because
                // `postData` swallows HTTP errors into a resolved `HttpError` (see class doc).
                // A truthy check is sufficient here since the backend's `SetCardState` action
                // always returns the updated card row on `200 OK` (see `CreditCardsController.
                // SetCardState`), so an `Ok(null)` body is not an expected shape in practice.
                if (response?.data) {
                    notify(`Card ${action === 'Pause' ? 'paused' : 'resumed'} successfully`, { type: 'success', autoHideDuration: 2000 });
                }
                else {
                    notify(`Failed to ${action.toLowerCase()} card: ${response?.message || response}`, { type: 'error', autoHideDuration: 5000 });
                }
                refresh();
            })
            .catch((error) => {
                setIsLoading(false);
                notify(`Failed to ${action.toLowerCase()} card: ${error.message || error}`, { type: 'error', autoHideDuration: 5000 });
                refresh();
            });
    };

    return (
        <Button size="small" disabled={isLoading} onClick={handleClick}>
            {action}
        </Button>
    );
};

export default React.memo(PauseResumeAction);
